import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  Info,
  Pencil,
  Plus,
  Save,
  Search,
  Sigma,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input, inputClass } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { usePermission } from '../../auth/usePermission';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { PermissionName } from '../../auth/permissions';
import { CategoryPicker } from '../../components/CategoryPicker';
import {
  coefficientFromMargin,
  fmtCoefficient,
  isSellableCoefficient,
  marginFromCoefficient,
  parseNum,
} from '../../lib/margin';

/**
 * Admin — Pricing grids.
 *
 *     selling price HT = NET PURCHASE PRICE × coefficient
 *
 * WHAT A CELL HOLDS. The family's own SELLING COEFFICIENT — not a discount off a shared base.
 * That distinction is the reason this screen was rewritten: a discount is only meaningful
 * against a base, the base had to be shared by every family, and a shared base is a number
 * nobody can ever touch because moving it re-prices everyone at once. Each cell is now
 * self-contained. Editing one family cannot move another.
 *
 * ONE BASE, ALWAYS: the net purchase price, after the supplier's discount. Not the gross
 * tariff, not the public price, not the weighted average cost. The reference ERP lets an
 * operator pick the base per column; we deliberately do not, because a grid whose columns
 * measure from different places cannot be read across.
 *
 * MARGIN AND COEFFICIENT, SIDE BY SIDE. The coefficient is stored, but the counter thinks in
 * margin, so the Marge/Coef toggle switches which one every cell shows and accepts — and both
 * are readable at once, the secondary figure sitting under the primary. The margin is margin ON
 * THE SELLING PRICE (coefficient = 1 / (1 − margin)); see src/lib/margin.ts for why that label
 * is spelled out in full everywhere it appears.
 *
 * ONE RULE WINS — they never stack. Three axes decide which, and the full ladder lives in
 * PriceGridRuleService; the short version:
 *
 *   WHO       a cell written for ONE NAMED ACCOUNT beats every cell written for its family.
 *             Pick a customer in the toolbar to get that account's own column beside the
 *             family columns; leave it empty and the grid is families only, as specified.
 *   WHAT      product 8 · brand 4 · supplier 2 · article family 1, summed — so brand+family
 *             beats brand beats supplier+family beats supplier beats article family.
 *   HOW MANY  a quantity bracket, 1–9 and 10+ being the usual pair. Only the bracket holding
 *             the line quantity is considered, and brackets may not overlap.
 *   nothing matched → the family default, edited on the family itself.
 *
 * AN EMPTY COLUMN MEANS "ALL", NOT "NO MATCH". A row with a brand and no article family prices
 * every product of that brand. The table says so on the row itself rather than in a legend.
 *
 * DISCOUNT IS NOT HERE. It is not configuration: the seller sees the margin at the counter and
 * moves it, usually down, to close the sale, with the net purchase price as the floor. That is
 * the only place a discount exists. See LineMargin in OrderCreate.
 */

interface Family {
  id: string;
  code: string;
  name: string;
  coefficient: number;
  marginPct: number | null;
  active: boolean;
  /** Migration 137 — not offered when a customer is created (a test family). */
  hidden: boolean;
  /** The customer types the onboarding form pre-fills this family for. */
  defaultFor: string[];
  rule_count: number;
  customer_count: number;
}

/** `label` is an i18n key, resolved at render time. */
const DEFAULT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'B2B', label: 'pricingGrids.customerTypes.B2B' },
  { value: 'B2C', label: 'pricingGrids.customerTypes.B2C' },
  { value: 'WALK_IN', label: 'pricingGrids.customerTypes.WALK_IN' },
];

interface Rule {
  id: string;
  price_grid_id: string | null;
  customer_id: string | null;
  supplier_id: string | null;
  brand: string | null;
  category_id: string | null;
  product_id: string | null;
  qty_min: number;
  qty_max: number | null;
  coefficient: number;
  marginPct: number | null;
  /** Resolved by PriceGridRuleService.list — see there for why the page must not look it up. */
  category_name: string | null;
  /** Root → node. Several TecDoc roots share a name, so the path is what identifies the node. */
  category_path: string | null;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
}

interface CustomerLite {
  id: string;
  code: string | null;
  name: string;
}

/**
 * How a row addresses products. PRODUCT rows are created from the product record, not here;
 * ALL is the targetless row a NAMED ACCOUNT may have ("this customer, everything") and which a
 * family may not — a family's targetless cell would be its default, and that lives on the family.
 */
type TargetKind =
  | 'BRAND_FAMILY'
  | 'BRAND'
  | 'SUPPLIER_FAMILY'
  | 'SUPPLIER'
  | 'ARTICLE_FAMILY'
  | 'PRODUCT'
  | 'ALL';

/**
 * A column of the grid: a customer family, or the one named account picked in the toolbar.
 * Writing a cell sends `priceGridId` for the first and `customerId` for the second — a cell
 * belongs to one scope or the other, never both.
 */
interface Column {
  key: string;
  id: string;
  code: string;
  name: string;
  scope: 'FAMILY' | 'CUSTOMER';
  /** Family columns only — the default coefficient shown in the pinned top row. */
  coefficient?: number;
  marginPct?: number | null;
  customerCount?: number;
}

interface GridRow {
  sig: string;
  kind: TargetKind;
  brand: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryPath: string | null;
  supplierId: string | null;
  productId: string | null;
  qtyMin: number;
  qtyMax: number | null;
  /** column key → the cell */
  cells: Record<string, Rule>;
}

/** "any qty", "1–9", "10+" — the bracket as a counter operator reads it. */
const bracketLabel = (t: TFunction, min: number, max: number | null): string =>
  max == null ? (min === 1 ? t('pricingGrids.anyQty') : `${min}+`) : `${min}–${max}`;

/** Which of the two linked figures the whole grid is currently showing and accepting. */
type Unit = 'COEF' | 'MARGIN';

const cmd = () => ({ headers: { 'X-Command-Id': crypto.randomUUID() } });

/* The bracket is PART of the row identity: "BOSCH 1-9" and "BOSCH 10+" are two rows with two
   coefficients, and folding them onto one row would show whichever the pivot happened to write
   last. It is part of the database key for the same reason. */
const sigOf = (r: Rule): string =>
  [
    r.product_id ? `p:${r.product_id}` : '',
    r.supplier_id ? `s:${r.supplier_id}` : '',
    (r.brand ?? '').toUpperCase(),
    r.category_id ?? '',
    `q${r.qty_min}-${r.qty_max ?? ''}`,
  ].join('|');

const kindOf = (r: Rule): TargetKind =>
  r.product_id
    ? 'PRODUCT'
    : r.brand
      ? r.category_id
        ? 'BRAND_FAMILY'
        : 'BRAND'
      : r.supplier_id
        ? r.category_id
          ? 'SUPPLIER_FAMILY'
          : 'SUPPLIER'
        : r.category_id
          ? 'ARTICLE_FAMILY'
          : 'ALL';

/** Most specific at the top, which is also the order the resolver reads them in. */
const KIND_RANK: Record<TargetKind, number> = {
  PRODUCT: 0,
  BRAND_FAMILY: 1,
  BRAND: 2,
  SUPPLIER_FAMILY: 3,
  SUPPLIER: 4,
  ARTICLE_FAMILY: 5,
  ALL: 6,
};

const KIND_CLASS: Record<TargetKind, string> = {
  BRAND_FAMILY: 'bg-primary/10 text-primary',
  BRAND: 'bg-success/15 text-success',
  SUPPLIER_FAMILY: 'bg-violet-500/15 text-violet-600',
  SUPPLIER: 'bg-violet-500/15 text-violet-600',
  ARTICLE_FAMILY: 'bg-blue-500/15 text-blue-600',
  PRODUCT: 'bg-amber-500/15 text-amber-600',
  ALL: 'bg-rose-500/15 text-rose-600',
};

/** What a part bought at this much sells for — the effect of a cell, in money. */
const SAMPLE_COST = 10;

const eur = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

/** Show BOTH figures always; `unit` only decides which is the big one. */
function CellFigures({ coefficient, unit }: { coefficient: number; unit: Unit }) {
  const margin = marginFromCoefficient(coefficient);
  const primary = unit === 'COEF' ? `×${fmtCoefficient(coefficient)}` : `${margin?.toFixed(2)} %`;
  const secondary = unit === 'COEF' ? `${margin?.toFixed(2)} %` : `×${fmtCoefficient(coefficient)}`;
  return (
    <span className="block leading-tight">
      <span className="block tabular-nums">{primary}</span>
      <span className="block text-[10px] tabular-nums text-muted-foreground">{secondary}</span>
    </span>
  );
}

/** The typed figure → the coefficient to store, honouring whichever unit the grid is in. */
function toCoefficient(raw: string, unit: Unit): number | null {
  const n = parseNum(raw);
  if (n == null) return null;
  const coef = unit === 'COEF' ? n : coefficientFromMargin(n);
  return coef != null && isSellableCoefficient(coef) ? coef : null;
}

const unitError = (t: TFunction, unit: Unit): string =>
  unit === 'COEF' ? t('pricingGrids.invalidCoefficient') : t('pricingGrids.invalidMargin');

export default function PricingGrids() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const canWrite = usePermission(PermissionName.PRICING_GRIDS_RW);

  const [families, setFamilies] = useState<Family[] | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filter, setFilter] = useState('');
  const [unit, setUnit] = useState<Unit>('COEF');
  /* The one named account whose OWN cells are shown as an extra column. Null is the normal
     state and the one the brief describes: columns are customer families. */
  const [customer, setCustomer] = useState<CustomerLite | null>(null);

  const [sheet, setSheet] = useState(false);
  const [familyModal, setFamilyModal] = useState(false);
  const [editing, setEditing] = useState<{ sig: string; columnKey: string; value: string } | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  const supName = useMemo(() => {
    const m: Record<string, string> = {};
    for (const x of suppliers) m[x.id] = x.name;
    return m;
  }, [suppliers]);

  const load = async () => {
    try {
      const [f, r] = await Promise.all([
        apiClient.get<{ data: Family[] }>('/price-grids/families'),
        apiClient.get<{ data: Rule[] }>('/price-grids/rules'),
      ]);
      setFamilies(f.data.data);
      setRules(r.data.data);
    } catch (err) {
      setFamilies([]);
      showApiError(err);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-line @typescript-eslint/no-floating-promises
    apiClient
      .get<{ data: Supplier[] }>('/suppliers')
      .then((res) => setSuppliers(res.data.data ?? []))
      .catch(() => undefined);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFamilies = useMemo(() => (families ?? []).filter((f) => f.active), [families]);

  /* The account's column goes FIRST, because it outranks every family column — the grid should
     read left to right in the same order the resolver reads. */
  const columns = useMemo<Column[]>(() => {
    const fam: Column[] = activeFamilies.map((f) => ({
      key: `family:${f.id}`,
      id: f.id,
      code: f.code,
      name: f.name,
      scope: 'FAMILY',
      coefficient: f.coefficient,
      marginPct: f.marginPct,
      customerCount: f.customer_count,
    }));
    if (!customer) return fam;
    return [
      {
        key: `customer:${customer.id}`,
        id: customer.id,
        code: customer.code || customer.name.slice(0, 8),
        name: customer.name,
        scope: 'CUSTOMER',
      },
      ...fam,
    ];
  }, [activeFamilies, customer]);

  /** Which column a cell belongs in — its scope decides, exactly as the resolver's does. */
  const columnKeyOf = (r: Rule): string =>
    r.customer_id ? `customer:${r.customer_id}` : `family:${r.price_grid_id}`;

  const labelOf = (row: GridRow): string => {
    // The name travels WITH the cell (PriceGridRuleService.list). It used to be looked up in the
    // catalogue's filter dropdown, which lists only families that have a product attached — so
    // every cell on any other TecDoc node read as a truncated UUID and could not be checked.
    const fam = row.categoryId ? (row.categoryName ?? row.categoryId.slice(0, 8)) : null;
    const sup = row.supplierId ? (supName[row.supplierId] ?? row.supplierId.slice(0, 8)) : null;
    if (row.kind === 'PRODUCT')
      return t('pricingGrids.productLabel', { id: row.productId!.slice(0, 8) });
    if (row.kind === 'BRAND_FAMILY') return `${row.brand} · ${fam}`;
    if (row.kind === 'BRAND') return row.brand!;
    if (row.kind === 'SUPPLIER_FAMILY') return `${sup} · ${fam}`;
    if (row.kind === 'SUPPLIER') return sup!;
    if (row.kind === 'ALL') return t('pricingGrids.kinds.ALL');
    return fam!;
  };

  // ---- pivot the cells into (target × column) rows ----
  const rows = useMemo<GridRow[]>(() => {
    const map = new Map<string, GridRow>();
    const shownKeys = new Set(columns.map((c) => c.key));
    for (const r of rules) {
      // A cell belonging to an account nobody has picked has no column to sit in; showing it
      // under its target with every cell blank would read as "this row is unpriced".
      if (!shownKeys.has(columnKeyOf(r))) continue;
      const sig = sigOf(r);
      let row = map.get(sig);
      if (!row) {
        row = {
          sig,
          kind: kindOf(r),
          brand: r.brand,
          categoryId: r.category_id,
          categoryName: r.category_name,
          categoryPath: r.category_path,
          supplierId: r.supplier_id,
          productId: r.product_id,
          qtyMin: r.qty_min,
          qtyMax: r.qty_max,
          cells: {},
        };
        map.set(sig, row);
      }
      row.cells[columnKeyOf(r)] = r;
    }
    const all = [...map.values()];
    const q = filter.trim().toLowerCase();
    const filtered = q ? all.filter((r) => labelOf(r).toLowerCase().includes(q)) : all;
    return filtered.sort(
      (a, b) =>
        KIND_RANK[a.kind] - KIND_RANK[b.kind] ||
        labelOf(a).localeCompare(labelOf(b)) ||
        a.qtyMin - b.qtyMin
    );
  }, [rules, supName, filter, columns]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The target half of a write. The scope half comes from the column — see saveCell. */
  const targetBody = (row: GridRow) => ({
    ...(row.kind === 'PRODUCT'
      ? { productId: row.productId }
      : { brand: row.brand, categoryId: row.categoryId, supplierId: row.supplierId }),
    qtyMin: row.qtyMin,
    qtyMax: row.qtyMax,
  });

  /** A column's scope, as the API expects it: one of the two, never both. */
  const scopeBody = (col: Column) =>
    col.scope === 'CUSTOMER' ? { customerId: col.id } : { priceGridId: col.id };

  // ---- inline cell edit ----
  const saveCell = async () => {
    if (!editing) return;
    const row = rows.find((r) => r.sig === editing.sig);
    if (!row) return;
    const col = columns.find((c) => c.key === editing.columnKey);
    if (!col) return;
    const raw = editing.value.trim();
    const existing = row.cells[editing.columnKey];
    setBusy(true);
    try {
      if (raw === '') {
        // Clearing a cell is not "0 %": it is "this column inherits from the next rule down".
        if (existing) await apiClient.delete(`/price-grids/rules/${existing.id}`, cmd());
      } else {
        const coefficient = toCoefficient(raw, unit);
        if (coefficient == null) {
          push({ tone: 'error', message: unitError(t, unit) });
          setBusy(false);
          return;
        }
        await apiClient.put(
          '/price-grids/rules',
          { ...scopeBody(col), ...targetBody(row), coefficient },
          cmd()
        );
      }
      setEditing(null);
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (row: GridRow) => {
    const n = Object.keys(row.cells).length;
    const ok = await confirm({
      title: t('pricingGrids.deleteRow.title', { label: labelOf(row) }),
      description: t('pricingGrids.deleteRow.description', { count: n }),
      confirmLabel: t('pricingGrids.deleteRow.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await Promise.all(
        Object.values(row.cells).map((c) => apiClient.delete(`/price-grids/rules/${c.id}`, cmd()))
      );
      push({ tone: 'success', message: t('pricingGrids.deleteRow.done') });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const colCount = columns.length + 3;

  return (
    <div>
      {confirmDialog}
      <PageHeader
        title={t('pricingGrids.title')}
        description={t('pricingGrids.description')}
        actions={
          canWrite && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setFamilyModal(true)}>
                <Users className="h-4 w-4" /> {t('pricingGrids.customerFamilies')}
              </Button>
              <Button
                size="sm"
                onClick={() => setSheet(true)}
                disabled={activeFamilies.length === 0}
              >
                <Plus className="h-4 w-4" /> {t('pricingGrids.addCoefficient')}
              </Button>
            </div>
          )
        }
      />

      {/* The two things an operator has to know before typing anything into this table, said
          once, at the top: what the coefficient multiplies, and what "margin" means here. */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-xs">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1">
          <p>
            <Trans i18nKey="pricingGrids.info.base" components={{ b: <b /> }} />
          </p>
          <p>
            <Trans
              i18nKey="pricingGrids.info.margin"
              components={{ b: <b />, num: <span className="tabular-nums" /> }}
            />
          </p>
          <p className="text-muted-foreground">
            <Trans i18nKey="pricingGrids.info.rules" components={{ b: <b /> }} />
          </p>
        </div>
      </div>

      {/* filter + the Marge/Coef toggle */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('pricingGrids.searchTarget')}
            className="pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {t('pricingGrids.rowCount', { count: rows.length })}
        </span>
        {/* Pick an account to get its OWN column, first and leftmost because it outranks every
            family column. Empty is the normal state: columns are customer families. */}
        <CustomerColumnPicker customer={customer} onPick={setCustomer} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('pricingGrids.showEditAs')}</span>
          <div className="flex overflow-hidden rounded-md border">
            <button
              type="button"
              onClick={() => setUnit('COEF')}
              className={`px-2.5 py-1 text-xs ${unit === 'COEF' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            >
              {t('pricingGrids.unitCoefficient')}
            </button>
            <button
              type="button"
              onClick={() => setUnit('MARGIN')}
              title={t('pricingGrids.marginTitle')}
              className={`px-2.5 py-1 text-xs ${unit === 'MARGIN' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            >
              {t('pricingGrids.unitMargin')}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="relative w-full overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              {/* Names the axis the columns run on. Without it the grid opens on a row of bare
                  codes. The empty cells are sticky-matched to the body's first column so the label
                  does not slide out from under the codes on horizontal scroll. */}
              <tr>
                <th className="sticky left-0 z-10 bg-muted/50 px-3 pt-2" />
                <th className="px-3 pt-2" />
                <th
                  colSpan={Math.max(1, columns.length)}
                  className="px-3 pt-2 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {t('pricingGrids.columnsAxis')}
                  {canWrite && (
                    <button
                      onClick={() => setFamilyModal(true)}
                      className="ml-1.5 font-medium normal-case tracking-normal text-primary underline-offset-2 hover:underline"
                    >
                      {t('pricingGrids.addOrRename')}
                    </button>
                  )}
                </th>
                {canWrite && <th className="pt-2" />}
              </tr>
              <tr>
                <th className="sticky left-0 z-10 h-10 bg-muted/50 px-3 text-left align-middle font-medium text-muted-foreground">
                  {t('pricingGrids.appliesTo')}
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  {t('pricingGrids.target')}
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    title={
                      c.scope === 'CUSTOMER'
                        ? t('pricingGrids.accountColumnTitle', { name: c.name })
                        : t('pricingGrids.familyColumnTitle', {
                            name: c.name,
                            coefficient: fmtCoefficient(c.coefficient!),
                            margin: c.marginPct,
                            count: c.customerCount,
                          })
                    }
                    className={`h-10 px-3 text-center align-middle font-medium ${
                      c.scope === 'CUSTOMER' ? 'bg-rose-500/10 text-rose-600' : 'text-foreground'
                    }`}
                  >
                    {/* Code, NAME, default — in that order of prominence. The name is what an
                        operator recognises ("Garages"); the code is how the rest of the app
                        refers to it; the default is the number every cell below overrides. */}
                    <div className="flex flex-col items-center leading-tight">
                      <span className="text-[10px] font-semibold uppercase tracking-wide opacity-60">
                        {c.code}
                      </span>
                      <span className="max-w-[10rem] truncate text-xs font-semibold" title={c.name}>
                        {c.name}
                      </span>
                      <span
                        className={`text-[10px] font-normal tabular-nums ${
                          c.scope === 'CUSTOMER' ? 'opacity-70' : 'text-muted-foreground'
                        }`}
                      >
                        {c.scope === 'CUSTOMER'
                          ? t('pricingGrids.thisAccount')
                          : unit === 'COEF'
                            ? t('pricingGrids.defaultCoef', {
                                coefficient: fmtCoefficient(c.coefficient!),
                              })
                            : t('pricingGrids.defaultMargin', { margin: c.marginPct })}
                      </span>
                    </div>
                  </th>
                ))}
                {canWrite && <th className="h-10 w-10 px-3" />}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {/* THE FAMILY DEFAULT, pinned above the rows that override it — it is the bottom of
                  the ladder, so a grid read top-down would otherwise never show what an
                  unmatched product costs. Edited on the family, which is its only home. */}
              <tr className="border-t bg-muted/20">
                <td className="sticky left-0 z-10 bg-muted/20 px-3 py-2 align-middle">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {t('pricingGrids.everythingElse')}
                  </span>
                </td>
                <td className="px-3 py-2 align-middle text-xs text-muted-foreground">
                  {t('pricingGrids.familyDefaultRow')}
                </td>
                {columns.map((c) =>
                  c.scope === 'CUSTOMER' ? (
                    // An account has no default of its own: with no cell anywhere it is priced
                    // by its family, and saying "—" here is what makes that visible.
                    <td
                      key={c.key}
                      title={t('pricingGrids.accountNoDefault')}
                      className="px-3 py-2 text-center align-middle text-muted-foreground/40"
                    >
                      —
                    </td>
                  ) : (
                    <td key={c.key} className="px-3 py-2 text-center align-middle">
                      <CellFigures coefficient={c.coefficient!} unit={unit} />
                    </td>
                  )
                )}
                {canWrite && (
                  <td className="px-2 py-2 text-right align-middle">
                    <button
                      onClick={() => setFamilyModal(true)}
                      className="p-1 text-muted-foreground hover:text-foreground"
                      title={t('pricingGrids.editFamilyDefaults')}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>

              {families === null && (
                <tr>
                  <td colSpan={colCount} className="px-3 py-6 text-center text-muted-foreground">
                    {t('pricingGrids.loading')}
                  </td>
                </tr>
              )}
              {families !== null && activeFamilies.length === 0 && (
                <tr>
                  <td
                    colSpan={colCount}
                    className="px-3 py-8 text-center text-sm text-muted-foreground"
                  >
                    <Users className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    {t('pricingGrids.noFamilyCta')}
                  </td>
                </tr>
              )}
              {families !== null && activeFamilies.length > 0 && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={colCount}
                    className="px-3 py-8 text-center text-sm text-muted-foreground"
                  >
                    <Sigma className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    {t('pricingGrids.noRows')}
                  </td>
                </tr>
              )}

              {rows.map((row) => (
                <tr key={row.sig} className="border-t transition-colors hover:bg-muted/40">
                  <td className="sticky left-0 z-10 bg-card px-3 py-2 align-middle">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${KIND_CLASS[row.kind]}`}
                    >
                      {t(`pricingGrids.kinds.${row.kind}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-middle font-medium">
                    <div className="truncate max-w-[20rem]" title={labelOf(row)}>
                      {labelOf(row)}
                    </div>
                    {/* Spelled out on the row, because "all" is what an empty column MEANS and a
                        legend at the bottom of the page is not where it gets read. */}
                    <div className="text-[10px] text-muted-foreground">
                      {/* The bracket first: it is the difference between two rows that otherwise
                          carry the same label, so it has to be readable without hovering. */}
                      <span className="mr-1 rounded bg-muted px-1 py-px font-medium tabular-nums">
                        {bracketLabel(t, row.qtyMin, row.qtyMax)}
                      </span>
                      {row.kind === 'BRAND' && t('pricingGrids.rowScope.BRAND')}
                      {row.kind === 'SUPPLIER' && t('pricingGrids.rowScope.SUPPLIER')}
                      {row.kind === 'ARTICLE_FAMILY' && t('pricingGrids.rowScope.ARTICLE_FAMILY')}
                      {row.kind === 'ALL' && t('pricingGrids.rowScope.ALL')}
                    </div>
                  </td>
                  {columns.map((c) => {
                    const cell = row.cells[c.key];
                    const isEditing = editing?.sig === row.sig && editing.columnKey === c.key;
                    if (isEditing) {
                      return (
                        <td key={c.key} className="px-1 py-1 text-center align-middle">
                          <div className="flex items-center justify-center gap-1">
                            <input
                              autoFocus
                              value={editing.value}
                              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveCell();
                                if (e.key === 'Escape') setEditing(null);
                              }}
                              className={inputClass + ' h-7 w-20 px-1.5 text-center'}
                              placeholder={unit === 'COEF' ? '1.5385' : '35'}
                            />
                            <button
                              onClick={saveCell}
                              disabled={busy}
                              className="text-success hover:opacity-70"
                              title={t('pricingGrids.save')}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={c.key}
                        title={
                          cell
                            ? t('pricingGrids.cellTitle', {
                                cost: eur(SAMPLE_COST),
                                price: eur(SAMPLE_COST * cell.coefficient),
                              })
                            : t('pricingGrids.inheritsNext')
                        }
                        onClick={() =>
                          canWrite &&
                          setEditing({
                            sig: row.sig,
                            columnKey: c.key,
                            value: cell
                              ? unit === 'COEF'
                                ? fmtCoefficient(cell.coefficient)
                                : String(marginFromCoefficient(cell.coefficient))
                              : '',
                          })
                        }
                        className={`px-3 py-2 text-center align-middle ${canWrite ? 'cursor-pointer' : ''} ${
                          cell ? '' : 'text-muted-foreground/40'
                        }`}
                      >
                        {cell ? (
                          <CellFigures coefficient={cell.coefficient} unit={unit} />
                        ) : canWrite ? (
                          '+'
                        ) : (
                          '—'
                        )}
                      </td>
                    );
                  })}
                  {canWrite && (
                    <td className="px-2 py-2 text-right align-middle">
                      <button
                        onClick={() => deleteRow(row)}
                        className="p-1 text-muted-foreground hover:text-destructive"
                        title={t('pricingGrids.deleteRow.confirm')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sheet && (
        <AddCoefficientSheet
          columns={columns}
          suppliers={suppliers}
          unit={unit}
          onClose={() => setSheet(false)}
          onSaved={load}
        />
      )}

      {familyModal && (
        <FamiliesModal
          families={families ?? []}
          unit={unit}
          onClose={() => setFamilyModal(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Add-coefficient sheet — one target and bracket, one value per column */
/* ------------------------------------------------------------------ */

function AddCoefficientSheet({
  columns,
  suppliers,
  unit,
  onClose,
  onSaved,
}: {
  columns: Column[];
  suppliers: Supplier[];
  unit: Unit;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [kind, setKind] = useState<Exclude<TargetKind, 'PRODUCT'>>('BRAND');
  const [brand, setBrand] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  /* ROOT → NODE for the family that was picked. The TecDoc tree carries SEVERAL roots called
     "Dispositif de freinage" (one per vehicle type) and the picker shows a bare name, so the
     name alone cannot tell an operator which of them they just chose — and a cell written on
     the wrong one silently prices nothing. Fetched once per pick; a failure leaves the name
     showing on its own rather than blocking the save. */
  const [categoryPath, setCategoryPath] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [qtyMin, setQtyMin] = useState('1');
  const [qtyMax, setQtyMax] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [pickCat, setPickCat] = useState(false);
  const [busy, setBusy] = useState(false);

  const labelCls = 'text-xs font-medium text-muted-foreground';
  const star = <span className="text-destructive">*</span>;
  const needsBrand = kind === 'BRAND' || kind === 'BRAND_FAMILY';
  const needsSupplier = kind === 'SUPPLIER' || kind === 'SUPPLIER_FAMILY';
  const needsCategory =
    kind === 'ARTICLE_FAMILY' || kind === 'BRAND_FAMILY' || kind === 'SUPPLIER_FAMILY';
  const chosenCategoryLabel = categoryPath || categoryName || categoryId;
  /* "Everything" is a targetless cell, and only a NAMED ACCOUNT may have one: a family's
     targetless cell would be its default, which has its own home on the family. */
  const customerColumn = columns.find((c) => c.scope === 'CUSTOMER') ?? null;

  const submit = async () => {
    if (needsBrand && !brand.trim()) {
      push({ tone: 'error', message: t('pricingGrids.sheet.errBrand') });
      return;
    }
    if (needsSupplier && !supplierId) {
      push({ tone: 'error', message: t('pricingGrids.sheet.errSupplier') });
      return;
    }
    if (needsCategory && !categoryId) {
      push({ tone: 'error', message: t('pricingGrids.sheet.errFamily') });
      return;
    }
    const min = Math.max(1, Math.floor(Number(qtyMin) || 1));
    const max = qtyMax.trim() === '' ? null : Math.floor(Number(qtyMax));
    if (max != null && (!Number.isFinite(max) || max < min)) {
      push({
        tone: 'error',
        message: t('pricingGrids.sheet.errBracket', { min, max: qtyMax }),
      });
      return;
    }
    const entries = Object.entries(values)
      .map(([key, v]) => [key, v.trim()] as const)
      .filter(([, v]) => v !== '');
    if (entries.length === 0) {
      push({ tone: 'error', message: t('pricingGrids.sheet.errNoValue') });
      return;
    }
    const payloads: Array<{ column: Column; coefficient: number }> = [];
    for (const [key, v] of entries) {
      const column = columns.find((c) => c.key === key);
      if (!column) continue;
      const coefficient = toCoefficient(v, unit);
      if (coefficient == null) {
        push({ tone: 'error', message: unitError(t, unit) });
        return;
      }
      payloads.push({ column, coefficient });
    }
    setBusy(true);
    try {
      await Promise.all(
        payloads.map((p) =>
          apiClient.put(
            '/price-grids/rules',
            {
              ...(p.column.scope === 'CUSTOMER'
                ? { customerId: p.column.id }
                : { priceGridId: p.column.id }),
              brand: needsBrand ? brand.trim() : null,
              supplierId: needsSupplier ? supplierId : null,
              categoryId: needsCategory ? categoryId : null,
              qtyMin: min,
              qtyMax: max,
              coefficient: p.coefficient,
            },
            cmd()
          )
        )
      );
      push({ tone: 'success', message: t('pricingGrids.sheet.saved') });
      onClose();
      await onSaved();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl space-y-4 rounded-lg border bg-card p-5 shadow-lg">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Sigma className="h-4 w-4 text-primary" /> {t('pricingGrids.addCoefficient')}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* left — what this row applies to */}
          <div className="space-y-3">
            <div className="space-y-1">
              <label className={labelCls}>
                {t('pricingGrids.appliesTo')} {star}
              </label>
              <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="BRAND">{t('pricingGrids.sheet.kindOptions.BRAND')}</option>
                <option value="BRAND_FAMILY">
                  {t('pricingGrids.sheet.kindOptions.BRAND_FAMILY')}
                </option>
                <option value="SUPPLIER">{t('pricingGrids.sheet.kindOptions.SUPPLIER')}</option>
                <option value="SUPPLIER_FAMILY">
                  {t('pricingGrids.sheet.kindOptions.SUPPLIER_FAMILY')}
                </option>
                <option value="ARTICLE_FAMILY">
                  {t('pricingGrids.sheet.kindOptions.ARTICLE_FAMILY')}
                </option>
                {customerColumn && (
                  <option value="ALL">{t('pricingGrids.sheet.kindOptions.ALL')}</option>
                )}
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {kind === 'ALL'
                  ? t('pricingGrids.sheet.kindHelp.ALL', {
                      name: customerColumn?.name ?? t('pricingGrids.thisAccount'),
                    })
                  : t(`pricingGrids.sheet.kindHelp.${kind}`)}
              </p>
            </div>

            <div className="space-y-1">
              <label className={labelCls}>
                {t('pricingGrids.sheet.brand')} {needsBrand && star}
              </label>
              <Input
                value={brand}
                disabled={!needsBrand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder={t('pricingGrids.sheet.brandPlaceholder')}
              />
            </div>

            <div className="space-y-1">
              <label className={labelCls}>
                {t('pricingGrids.sheet.supplier')} {needsSupplier && star}
              </label>
              <Select
                value={supplierId}
                disabled={!needsSupplier}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">{t('pricingGrids.sheet.select')}</option>
                {suppliers.map((sup) => (
                  <option key={sup.id} value={sup.id}>
                    {sup.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1">
              <label className={labelCls}>
                {t('pricingGrids.sheet.articleFamily')} {needsCategory && star}
              </label>
              <button
                type="button"
                disabled={!needsCategory}
                onClick={() => setPickCat(true)}
                className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-left text-sm hover:bg-accent disabled:opacity-50"
              >
                <span className={`truncate ${categoryId ? '' : 'text-muted-foreground'}`}>
                  {categoryId ? chosenCategoryLabel : t('pricingGrids.sheet.chooseFamily')}
                </span>
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>

            {/* THE QUANTITY BRACKET. Their sheet prints +++++ for an open top; an empty
                Maximum is the same thing, and it is spelled out under the field because a
                blank that means "unbounded" is otherwise indistinguishable from a blank
                that means "not filled in yet". */}
            <div className="space-y-1">
              <label className={labelCls}>{t('pricingGrids.sheet.bracket')}</label>
              <div className="flex items-center gap-2">
                <Input
                  value={qtyMin}
                  onChange={(e) => setQtyMin(e.target.value)}
                  placeholder="1"
                  className="w-20 tabular-nums"
                />
                <span className="text-xs text-muted-foreground">{t('pricingGrids.sheet.to')}</span>
                <Input
                  value={qtyMax}
                  onChange={(e) => setQtyMax(e.target.value)}
                  placeholder="∞"
                  className="w-20 tabular-nums"
                />
                <span className="text-xs text-muted-foreground">
                  {bracketLabel(
                    t,
                    Math.max(1, Math.floor(Number(qtyMin) || 1)),
                    qtyMax.trim() === '' ? null : Math.floor(Number(qtyMax))
                  )}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('pricingGrids.sheet.bracketHelp')}
              </p>
            </div>
          </div>

          {/* right — one figure per customer family */}
          <div className="space-y-2">
            <label className={labelCls}>
              {unit === 'COEF'
                ? t('pricingGrids.sheet.valuesCoef')
                : t('pricingGrids.sheet.valuesMargin')}
            </label>
            <div className="grid max-h-[18rem] grid-cols-1 gap-2 overflow-auto rounded-md border bg-muted/20 p-3">
              {columns
                // "Everything" is an account-only shape, so only that column can take a value.
                .filter((c) => kind !== 'ALL' || c.scope === 'CUSTOMER')
                .map((c) => {
                  const coef = toCoefficient(values[c.key] ?? '', unit);
                  return (
                    <div key={c.key} className="flex items-center gap-2">
                      <span
                        className={`w-10 shrink-0 text-xs font-medium ${c.scope === 'CUSTOMER' ? 'text-rose-600' : ''}`}
                        title={c.name}
                      >
                        {c.code}
                      </span>
                      <input
                        value={values[c.key] ?? ''}
                        onChange={(e) => setValues({ ...values, [c.key]: e.target.value })}
                        placeholder={
                          c.coefficient == null
                            ? ''
                            : unit === 'COEF'
                              ? fmtCoefficient(c.coefficient)
                              : String(c.marginPct ?? '')
                        }
                        className={inputClass + ' h-8 w-24 px-2 tabular-nums'}
                      />
                      {/* The cell in money, live: a coefficient is abstract, ten euros is not. */}
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {coef
                          ? `${eur(SAMPLE_COST)} → ${eur(SAMPLE_COST * coef)} HT · ×${fmtCoefficient(coef)}`
                          : t('pricingGrids.sheet.leaveEmpty')}
                      </span>
                    </div>
                  );
                })}
              {columns.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('pricingGrids.sheet.noFamilyDefined')}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button size="sm" variant="outline" disabled={busy} onClick={onClose}>
            <X className="h-4 w-4" /> {t('pricingGrids.cancel')}
          </Button>
          <Button size="sm" disabled={busy} onClick={submit}>
            <Save className="h-4 w-4" /> {busy ? t('pricingGrids.saving') : t('pricingGrids.save')}
          </Button>
        </div>
      </div>

      {pickCat && (
        <CategoryPicker
          onClose={() => setPickCat(false)}
          onPick={(id, name) => {
            setCategoryId(id ?? '');
            setCategoryName(id ? name : '');
            setCategoryPath('');
            setPickCat(false);
            if (id) {
              apiClient
                .get<{ id: string; name: string }[]>(`/products/categories/${id}/breadcrumb`)
                .then((res) => setCategoryPath(res.data.map((c) => c.name).join(' › ')))
                .catch(() => undefined);
            }
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Customer families — the columns, and their default coefficient.     */
/* ------------------------------------------------------------------ */

function FamiliesModal({
  families,
  unit,
  onClose,
  onChanged,
}: {
  families: Family[];
  unit: Unit;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [draft, setDraft] = useState({ code: '', name: '', value: '' });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const code = draft.code.trim().toUpperCase();
    const name = draft.name.trim();
    if (!/^[A-Z0-9_-]{1,10}$/.test(code) || !name) {
      push({ tone: 'error', message: t('pricingGrids.families.errCodeName') });
      return;
    }
    const coefficient = toCoefficient(draft.value, unit);
    if (coefficient == null) {
      push({ tone: 'error', message: unitError(t, unit) });
      return;
    }
    setBusy(true);
    try {
      await apiClient.post('/price-grids/families', { code, name, coefficient }, cmd());
      setDraft({ code: '', name: '', value: '' });
      await onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const del = async (f: Family) => {
    const ok = await confirm({
      title: t('pricingGrids.families.deleteTitle', { code: f.code }),
      description: t('pricingGrids.families.deleteDescription', { count: f.rule_count }),
      confirmLabel: t('pricingGrids.families.deleteConfirm'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.delete(`/price-grids/families/${f.id}`, cmd());
      await onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {confirmDialog}
      <div className="w-full max-w-2xl space-y-4 rounded-lg border bg-card p-5 shadow-lg">
        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Users className="h-4 w-4 text-primary" /> {t('pricingGrids.customerFamilies')}
            </h2>
            <p className="text-xs text-muted-foreground">{t('pricingGrids.families.intro')}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-80 space-y-2 overflow-auto">
          {families.map((f) => (
            <FamilyRow
              key={f.id}
              family={f}
              unit={unit}
              busy={busy}
              setBusy={setBusy}
              onChanged={onChanged}
              onDelete={del}
            />
          ))}
          {families.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t('pricingGrids.families.none')}
            </p>
          )}
        </div>

        <div className="flex items-end gap-2 border-t pt-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('pricingGrids.families.code')}
            </label>
            <Input
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              placeholder="K5"
              className="w-20"
            />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('pricingGrids.families.name')}
            </label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('pricingGrids.families.namePlaceholder')}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {unit === 'COEF'
                ? t('pricingGrids.families.defaultCoef')
                : t('pricingGrids.families.defaultMargin')}
            </label>
            <Input
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder={unit === 'COEF' ? '1.5385' : '35'}
              className="w-24"
            />
          </div>
          <Button size="sm" disabled={busy} onClick={add}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FamilyRow({
  family,
  unit,
  busy,
  setBusy,
  onChanged,
  onDelete,
}: {
  family: Family;
  unit: Unit;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onChanged: () => Promise<void>;
  onDelete: (f: Family) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(family.name);
  const [value, setValue] = useState(
    unit === 'COEF' ? fmtCoefficient(family.coefficient) : String(family.marginPct ?? '')
  );

  // The toggle can flip while a row sits open; the field has to follow it or it would be read
  // as the other unit and saved as a wildly different coefficient.
  useEffect(() => {
    setValue(unit === 'COEF' ? fmtCoefficient(family.coefficient) : String(family.marginPct ?? ''));
  }, [unit, family.coefficient, family.marginPct]);

  const save = async () => {
    const coefficient = toCoefficient(value, unit);
    if (coefficient == null) {
      push({ tone: 'error', message: unitError(t, unit) });
      return;
    }
    setBusy(true);
    try {
      await apiClient.put(
        `/price-grids/families/${family.id}`,
        { name: name.trim(), coefficient, active: family.active },
        cmd()
      );
      setEdit(false);
      await onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await apiClient.put(
        `/price-grids/families/${family.id}`,
        { name: family.name, coefficient: family.coefficient, active: !family.active },
        cmd()
      );
      await onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Hidden / default-for (migration 137): one PUT carrying the family as it stands. */
  const setFlags = async (patch: { hidden?: boolean; defaultFor?: string[] }) => {
    setBusy(true);
    try {
      await apiClient.put(
        `/price-grids/families/${family.id}`,
        { name: family.name, coefficient: family.coefficient, ...patch },
        cmd()
      );
      await onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  if (edit) {
    return (
      <div className="flex items-center gap-2 rounded border px-2 py-1.5">
        <span className="w-10 text-xs font-medium">{family.code}</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 flex-1" />
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-8 w-24 tabular-nums"
        />
        <button
          onClick={save}
          disabled={busy}
          className="text-success hover:opacity-70"
          title={t('pricingGrids.save')}
        >
          <Check className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 rounded border px-2 py-1.5 text-sm ${family.active ? '' : 'opacity-50'}`}
    >
      <span className="w-10 font-medium">{family.code}</span>
      <span className="flex-1 truncate">{family.name}</span>
      {/* Both figures, always — this row IS the family default cell. */}
      <span className="tabular-nums text-muted-foreground">
        ×{fmtCoefficient(family.coefficient)}
        <span className="ml-1 text-[10px]">
          {t('pricingGrids.families.marginShort', { margin: family.marginPct })}
        </span>
      </span>
      <span className="w-24 text-right text-[11px] text-muted-foreground">
        {t('pricingGrids.families.customerCount', { count: family.customer_count })}
      </span>
      <select
        className="h-6 rounded border bg-background px-1 text-[10px]"
        value={family.defaultFor[0] ?? ''}
        disabled={busy || family.hidden}
        title={t('pricingGrids.families.defaultForTitle')}
        onChange={(e) => {
          const type = e.target.value;
          // WALK_IN rides with B2C: both are retail.
          void setFlags({
            defaultFor: !type ? [] : type === 'B2C' ? ['B2C', 'WALK_IN'] : [type],
          });
        }}
      >
        <option value="">{t('pricingGrids.families.noDefault')}</option>
        {DEFAULT_TYPES.filter((d) => d.value !== 'WALK_IN').map((d) => (
          <option key={d.value} value={d.value}>
            {t('pricingGrids.families.defaultFor', { type: t(d.label) })}
          </option>
        ))}
      </select>
      <button
        onClick={() =>
          void setFlags({ hidden: !family.hidden, ...(family.hidden ? {} : { defaultFor: [] }) })
        }
        disabled={busy}
        className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
        title={
          family.hidden
            ? t('pricingGrids.families.hiddenTitle')
            : t('pricingGrids.families.listedTitle')
        }
      >
        {family.hidden ? t('pricingGrids.families.hidden') : t('pricingGrids.families.listed')}
      </button>
      <button
        onClick={toggleActive}
        disabled={busy}
        className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
        title={
          family.active
            ? t('pricingGrids.families.deactivateTitle')
            : t('pricingGrids.families.reactivate')
        }
      >
        {family.active ? t('pricingGrids.families.active') : t('pricingGrids.families.inactive')}
      </button>
      <button
        onClick={() => setEdit(true)}
        className="p-1 text-muted-foreground hover:text-foreground"
        title={t('pricingGrids.edit')}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onDelete(family)}
        className="p-1 text-muted-foreground hover:text-destructive"
        title={t('pricingGrids.delete')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Customer column picker — the "Code Client" axis of the grid.        */
/* ------------------------------------------------------------------ */

/**
 * Pick one account and its OWN cells appear as an extra, leftmost column.
 *
 * Why a picker rather than a column per customer: there are hundreds of accounts and four
 * families, so a column each would be a grid nobody can read, and the point of families is
 * precisely that most accounts do not need their own numbers. An account cell is the
 * exception — "this one garage negotiated 22 % on Bosch" — and an exception belongs behind a
 * deliberate act, not in everyone's default view.
 */
function CustomerColumnPicker({
  customer,
  onPick,
}: {
  customer: CustomerLite | null;
  onPick: (c: CustomerLite | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CustomerLite[]>([]);

  // Debounced, because the list endpoint scans name and code with a LIKE and the picker is
  // typed into at counter speed.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    const timer = setTimeout(() => {
      apiClient
        .get<{ rows: CustomerLite[] }>(
          `/customers?limit=20${term ? `&q=${encodeURIComponent(term)}` : ''}`
        )
        .then((r) => setHits(r.data.rows ?? []))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [q, open]);

  if (customer) {
    return (
      <span className="flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs text-rose-600">
        <UserRound className="h-3.5 w-3.5" />
        <span className="max-w-[14rem] truncate" title={customer.name}>
          {customer.code ? `${customer.code} — ` : ''}
          {customer.name}
        </span>
        <button
          onClick={() => onPick(null)}
          className="ml-1 hover:opacity-70"
          title={t('pricingGrids.picker.remove')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
      >
        <UserRound className="h-3.5 w-3.5" /> {t('pricingGrids.picker.add')}
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-80 rounded-md border bg-card p-2 shadow-lg">
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('pricingGrids.picker.search')}
            className="h-8"
          />
          <div className="mt-2 max-h-60 overflow-auto">
            {hits.map((h) => (
              <button
                key={h.id}
                onClick={() => {
                  onPick(h);
                  setOpen(false);
                  setQ('');
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-accent"
              >
                <span className="w-14 shrink-0 font-medium">{h.code ?? '—'}</span>
                <span className="truncate">{h.name}</span>
              </button>
            ))}
            {hits.length === 0 && (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                {q.trim()
                  ? t('pricingGrids.picker.noMatch')
                  : t('pricingGrids.picker.typeToSearch')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
